import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, Pencil } from 'lucide-react'
import { useVehicle, useActivateVehicle, useDeactivateVehicle } from '@/hooks/useVehicles'
import { StatusBadge } from '@/components/ui/status-badge'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Button } from '@/components/ui/button'
import { ExpiryBanner } from '@/components/ui/expiry-banner'
import { maskPlate } from '@/components/form/masks'
import { VehicleFinancialSummary } from './VehicleFinancialSummary'
import { usePermissions } from '@/hooks/usePermissions'

const VEHICLE_TYPE_LABEL: Record<string, string> = {
  FIORINO: 'Fiorino',
  VAN: 'Van',
  VUC: 'VUC',
  TOCO: 'Toco',
  TRUCK: 'Truck',
  BITRUCK: 'Bitruck',
  CARRETA: 'Carreta',
  RODOTREM: 'Rodotrem',
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
}

function DetailRow({ label, value }: Readonly<{ label: string; value?: string | null }>) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-foreground">{value || '—'}</dd>
    </div>
  )
}

export function VehicleDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { data: vehicle, isLoading } = useVehicle(id ?? '')
  const activate = useActivateVehicle()
  const deactivate = useDeactivateVehicle()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { has } = usePermissions()

  const isPending = activate.isPending || deactivate.isPending

  async function handleToggle() {
    if (!vehicle) return
    try {
      if (vehicle.active) {
        await deactivate.mutateAsync(vehicle.id)
        toast.success('Veículo desativado')
      } else {
        await activate.mutateAsync(vehicle.id)
        toast.success('Veículo ativado')
      }
    } catch {
      toast.error('Erro ao alterar status do veículo')
    } finally {
      setConfirmOpen(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    )
  }

  if (!vehicle) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Veículo não encontrado.</p>
      </div>
    )
  }

  const hasCapacity = vehicle.capacityKg || vehicle.bodyLength || vehicle.bodyWidth || vehicle.bodyHeight
  const hasDocs = vehicle.crlvNumber || vehicle.crlvExpiry || vehicle.insuranceCompany || vehicle.insurancePolicy || vehicle.insuranceExpiry
  const canSeeFinancialSummary = has('vehicle.view_financial_summary')

  const ownerLabel =
    vehicle.ownership === 'COMPANY'
      ? 'Empresa'
      : vehicle.owner?.name ?? 'Motorista'

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon-sm" onClick={() => navigate('/vehicles')}>
          <ArrowLeft size={16} />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-mono text-xl font-semibold text-foreground truncate">
              {maskPlate(vehicle.plate)}
            </h1>
            <StatusBadge active={vehicle.active} />
          </div>
          <p className="text-sm text-muted-foreground truncate">
            {VEHICLE_TYPE_LABEL[vehicle.vehicleType] ?? vehicle.vehicleType}
            {vehicle.brand && ` · ${vehicle.brand}`}
            {vehicle.model && ` ${vehicle.model}`}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/vehicles/${vehicle.id}/edit`)}
        >
          <Pencil size={14} />
          Editar
        </Button>
      </div>

      <ExpiryBanner
        items={[
          {
            date: vehicle.crlvExpiry,
            label: 'CRLV',
            detail: vehicle.crlvNumber ? `Nº ${vehicle.crlvNumber}` : undefined,
          },
          {
            date: vehicle.insuranceExpiry,
            label: 'Seguro',
            detail: vehicle.insuranceCompany ?? undefined,
          },
        ]}
      />

      <div className="space-y-5 rounded-lg border border-border p-5">
        <div>
          <h2 className="mb-3 text-sm font-medium text-foreground">Identificação</h2>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <DetailRow label="Placa" value={maskPlate(vehicle.plate)} />
            <DetailRow label="RENAVAM" value={vehicle.renavam} />
            <DetailRow label="RNTRC / ANTT" value={vehicle.rntrc} />
            <DetailRow label="Marca" value={vehicle.brand} />
            <DetailRow label="Modelo" value={vehicle.model} />
            <DetailRow label="Ano" value={vehicle.year?.toString()} />
          </dl>
        </div>

        <div className="border-t border-border pt-4">
          <h2 className="mb-3 text-sm font-medium text-foreground">Proprietário</h2>
          <dl className="grid grid-cols-2 gap-4">
            <DetailRow
              label="Propriedade"
              value={vehicle.ownership === 'COMPANY' ? 'Empresa' : 'Motorista'}
            />
            {vehicle.ownership === 'DRIVER' && (
              <DetailRow label="Motorista" value={ownerLabel} />
            )}
          </dl>
        </div>

        {hasCapacity && (
          <div className="border-t border-border pt-4">
            <h2 className="mb-3 text-sm font-medium text-foreground">Capacidade</h2>
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <DetailRow
                label="Peso máx. (kg)"
                value={vehicle.capacityKg ? Number.parseFloat(vehicle.capacityKg).toLocaleString('pt-BR') : null}
              />
              <DetailRow
                label="Compr. (m)"
                value={vehicle.bodyLength ?? null}
              />
              <DetailRow
                label="Larg. (m)"
                value={vehicle.bodyWidth ?? null}
              />
              <DetailRow
                label="Alt. (m)"
                value={vehicle.bodyHeight ?? null}
              />
            </dl>
          </div>
        )}

        {hasDocs && (
          <div className="border-t border-border pt-4">
            <h2 className="mb-3 text-sm font-medium text-foreground">Documentos</h2>
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <DetailRow label="Nº CRLV" value={vehicle.crlvNumber} />
              <DetailRow label="Validade CRLV" value={formatDate(vehicle.crlvExpiry)} />
              <div />
              <DetailRow label="Seguradora" value={vehicle.insuranceCompany} />
              <DetailRow label="Apólice" value={vehicle.insurancePolicy} />
              <DetailRow label="Validade seguro" value={formatDate(vehicle.insuranceExpiry)} />
            </dl>
          </div>
        )}
      </div>

      {canSeeFinancialSummary && <VehicleFinancialSummary vehicleId={vehicle.id} />}

      <div className="flex justify-end">
        <Button
          variant={vehicle.active ? 'destructive' : 'default'}
          onClick={() => setConfirmOpen(true)}
          disabled={isPending}
        >
          {vehicle.active ? 'Desativar veículo' : 'Ativar veículo'}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title={vehicle.active ? 'Desativar veículo?' : 'Ativar veículo?'}
        description={
          vehicle.active
            ? `O veículo "${maskPlate(vehicle.plate)}" ficará inativo e não aparecerá em novos fretes.`
            : `O veículo "${maskPlate(vehicle.plate)}" voltará a ficar disponível.`
        }
        confirmLabel={vehicle.active ? 'Desativar' : 'Ativar'}
        destructive={vehicle.active}
        loading={isPending}
        onConfirm={handleToggle}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  )
}
